import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import MatchAttachmentField, { type MatchAttachmentFieldState } from './MatchAttachmentField';
import { MatchRole, resolveEnum } from '@/models/tournament';
import type { MatchView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';
import { appDate, appTime } from '@/utils/appDateFormat';

interface CompleteMatchFormData {
  team1Score: string;
  team2Score: string;
  extraTimeTeam1Score: string;
  extraTimeTeam2Score: string;
  penaltyTeam1Score: string;
  penaltyTeam2Score: string;
  comments: string;
}

interface FormErrors {
  [key: string]: string;
}

interface SubmitStatus {
  type: string;
  message: string;
}

interface CompletionData {
  matchId: number;
  team1Score: number;
  team2Score: number;
  extraTimeTeam1Score?: number;
  extraTimeTeam2Score?: number;
  penaltyTeam1Score?: number;
  penaltyTeam2Score?: number;
  comments: string;
  refereeRatings?: { refereeTeamMemberId: number; rating: number }[];
  /** Attachment changes staged in the modal. Parent handler applies them via Match/UpdateAttachment after completeMatch succeeds. */
  attachment?: { file: File | null; remove: boolean; dirty: boolean };
}

interface CompleteMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: MatchView | null;
  onComplete: (data: CompletionData) => void;
  /** Hide referee-rating UI when completer is a referee (non-HR). */
  isReferee?: boolean;
  /**
   * Owner decision 2026-09-08: nobody rates their own performance — the rating feeds the work-point
   * performance bonus. The current user's own assignment never gets a star row, whoever they are.
   */
  currentUserId?: string;
  /**
   * Overrides `isReferee` for the rating section only: HR/Admin and the match's own Commissioner may
   * rate the other officials. Defaults to the `isReferee` behaviour when not supplied.
   */
  canRateReferees?: boolean;
}

const CompleteMatchModal: React.FC<CompleteMatchModalProps> = ({ isOpen, onClose, match, onComplete, isReferee, currentUserId, canRateReferees }) => {
  const { t } = useTranslation('tournament');
  const [formData, setFormData] = useState<CompleteMatchFormData>({
    team1Score: '',
    team2Score: '',
    extraTimeTeam1Score: '',
    extraTimeTeam2Score: '',
    penaltyTeam1Score: '',
    penaltyTeam2Score: '',
    comments: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });
  const [refereeRatings, setRefereeRatings] = useState<Record<number, number>>({});
  const [showExtraTime, setShowExtraTime] = useState(false);
  const [showPenalties, setShowPenalties] = useState(false);
  const [attachmentState, setAttachmentState] = useState<MatchAttachmentFieldState>({ file: null, remove: false, dirty: false });
  const handleAttachmentChange = useCallback((s: MatchAttachmentFieldState) => setAttachmentState(s), []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData?.team1Score || isNaN(parseInt(formData?.team1Score)) || parseInt(formData?.team1Score) < 0) {
      newErrors.team1Score = t('Valid score required');
    }

    if (!formData?.team2Score || isNaN(parseInt(formData?.team2Score)) || parseInt(formData?.team2Score) < 0) {
      newErrors.team2Score = t('Valid score required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const roleKeys: Record<number, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const handleRatingChange = (memberId: number, rating: number) => {
    setRefereeRatings(prev => {
      if (prev[memberId] === rating) {
        const next = { ...prev };
        delete next[memberId];
        return next;
      }
      return { ...prev, [memberId]: rating };
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Never emit a rating for the caller's own assignment, whatever the UI state was.
      const ownMemberIds = new Set(
        (match?.refereeTeamMembers ?? [])
          .filter((m) => currentUserId && m.userId === currentUserId)
          .map((m) => m.id),
      );
      const ratings = Object.entries(refereeRatings)
        .map(([id, rating]) => ({ refereeTeamMemberId: parseInt(id), rating }))
        .filter((r) => !ownMemberIds.has(r.refereeTeamMemberId));

      const parseOptional = (val: string): number | undefined => {
        if (!val) return undefined;
        const n = parseInt(val);
        return isNaN(n) ? undefined : n;
      };

      onComplete?.({
        matchId: match?.id ?? 0,
        team1Score: parseInt(formData?.team1Score),
        team2Score: parseInt(formData?.team2Score),
        extraTimeTeam1Score: parseOptional(formData.extraTimeTeam1Score),
        extraTimeTeam2Score: parseOptional(formData.extraTimeTeam2Score),
        penaltyTeam1Score: parseOptional(formData.penaltyTeam1Score),
        penaltyTeam2Score: parseOptional(formData.penaltyTeam2Score),
        comments: formData?.comments,
        refereeRatings: ratings.length > 0 ? ratings : undefined,
        attachment: attachmentState,
      });

      setFormData({ team1Score: '', team2Score: '', extraTimeTeam1Score: '', extraTimeTeam2Score: '', penaltyTeam1Score: '', penaltyTeam2Score: '', comments: '' });
      setRefereeRatings({});
      setAttachmentState({ file: null, remove: false, dirty: false });
      setSubmitStatus({ type: '', message: '' });
      onClose();
    } catch {
      setSubmitStatus({ type: 'error', message: t('Failed to complete match. Error.') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    setFormData({ team1Score: '', team2Score: '', extraTimeTeam1Score: '', extraTimeTeam2Score: '', penaltyTeam1Score: '', penaltyTeam2Score: '', comments: '' });
    setErrors({});
    setSubmitStatus({ type: '', message: '' });
    setRefereeRatings({});
    setShowExtraTime(false);
    setShowPenalties(false);
    onClose();
  };

  if (!isOpen || !match) return null;

  const canRate = canRateReferees ?? !isReferee;
  // Self-rating is what made T-03 pay: the star you give yourself feeds the performance bonus on the
  // very points this dialog is about to compute. Drop your own row, never disable it in place.
  const ratableMembers = (match.refereeTeamMembers ?? []).filter(
    (m) => !currentUserId || m.userId !== currentUserId,
  );

  const formatDateTime = (dateTimeString: string): { date: string; time: string } => {
    const date = parseApiWallClock(dateTimeString) ?? new Date(dateTimeString);
    return {
      date: appDate(date),
      time: appTime(date)
    };
  };

  return (
    // Every dismissal path (X, Escape, backdrop, Cancel) is locked together while the submission
    // is in flight — the X button was already disabled, the other three now behave the same.
    <Dialog open onOpenChange={(open) => { if (!open && !isSubmitting) handleClose(); }}>
      {/* The X below is kept (rather than Dialog's built-in close) because it must disable during
          submission and because E2E specs address it by data-testid. */}
      <DialogContent size="lg" hideCloseButton data-testid="complete-match-modal">
        <DialogHeader className="sticky top-0 bg-card py-4 z-10">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center">
              <Icon name="CheckCircle" size={24} className="me-2 text-green-600" />
              {t('Submit Match as Complete')}
            </DialogTitle>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('Close')}
              className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
              disabled={isSubmitting}
              data-testid="complete-match-close-btn"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* Match Info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{t('Match ID')}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(match?.scheduledDateTime ?? '')?.date} • {formatDateTime(match?.scheduledDateTime ?? '')?.time}</p>
              </div>
              <p className="text-base font-semibold text-foreground">{match?.id}</p>
              <div className="mt-2 flex items-center space-x-2 rtl:space-x-reverse">
                <Icon name="MapPin" size={14} className="text-gray-400" />
                <span className="text-sm text-muted-foreground">{match?.location}</span>
              </div>
            </div>

            {/* Score Entry */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">{t('Final Score *')}</h3>

              <div className="bg-gradient-to-r from-primary/10 to-blue-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 items-center">
                  {/* Team 1 */}
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2 text-center">
                      {match?.team1Name}
                    </p>
                    <Input
                      type="number"
                      name="team1Score"
                      value={formData?.team1Score}
                      onChange={handleInputChange}
                      error={errors?.team1Score}
                      placeholder="0"
                      min="0"
                      className="text-center text-2xl font-bold"
                      disabled={isSubmitting}
                      data-testid="complete-match-team1-score"
                    />
                  </div>

                  {/* VS */}
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-400">{t('VS')}</p>
                  </div>

                  {/* Team 2 */}
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2 text-center">
                      {match?.team2Name}
                    </p>
                    <Input
                      type="number"
                      name="team2Score"
                      value={formData?.team2Score}
                      onChange={handleInputChange}
                      error={errors?.team2Score}
                      placeholder="0"
                      min="0"
                      className="text-center text-2xl font-bold"
                      disabled={isSubmitting}
                      data-testid="complete-match-team2-score"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Extra Time (Optional) */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowExtraTime(prev => !prev)}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
              >
                {/* Only the collapsed (horizontal) chevron mirrors in RTL — rotating the open-state
                    ChevronDown would point it up, which reads as "collapse" in every locale. */}
                <Icon
                  name={showExtraTime ? 'ChevronDown' : 'ChevronRight'}
                  size={16}
                  className={showExtraTime ? undefined : 'rtl:rotate-180'}
                />
                {t('Extra Time Score')}
              </button>
              {showExtraTime && (
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 items-center">
                    <div>
                      <p className="text-xs font-medium text-foreground mb-1.5 text-center">{match?.team1Name}</p>
                      <Input
                        type="number"
                        name="extraTimeTeam1Score"
                        value={formData.extraTimeTeam1Score}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        className="text-center text-xl font-bold"
                        disabled={isSubmitting}
                        data-testid="complete-match-et-team1-score"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-amber-500">{t('ET')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground mb-1.5 text-center">{match?.team2Name}</p>
                      <Input
                        type="number"
                        name="extraTimeTeam2Score"
                        value={formData.extraTimeTeam2Score}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        className="text-center text-xl font-bold"
                        disabled={isSubmitting}
                        data-testid="complete-match-et-team2-score"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Penalties (Optional) */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowPenalties(prev => !prev)}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
              >
                <Icon
                  name={showPenalties ? 'ChevronDown' : 'ChevronRight'}
                  size={16}
                  className={showPenalties ? undefined : 'rtl:rotate-180'}
                />
                {t('Penalty Score')}
              </button>
              {showPenalties && (
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 items-center">
                    <div>
                      <p className="text-xs font-medium text-foreground mb-1.5 text-center">{match?.team1Name}</p>
                      <Input
                        type="number"
                        name="penaltyTeam1Score"
                        value={formData.penaltyTeam1Score}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        className="text-center text-xl font-bold"
                        disabled={isSubmitting}
                        data-testid="complete-match-pen-team1-score"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-red-500">{t('PEN')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground mb-1.5 text-center">{match?.team2Name}</p>
                      <Input
                        type="number"
                        name="penaltyTeam2Score"
                        value={formData.penaltyTeam2Score}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        className="text-center text-xl font-bold"
                        disabled={isSubmitting}
                        data-testid="complete-match-pen-team2-score"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rate Referees (Optional) — HR/Admin and the match commissioner, never oneself */}
            {canRate && ratableMembers.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">{t('Rate Referee Performance')}</h3>
                <div className="space-y-3">
                  {ratableMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="User" size={16} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{member.userName}</p>
                          <p className="text-xs text-muted-foreground">{t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')}</p>
                        </div>
                      </div>
                      {/* Selection was conveyed by fill colour alone and every star was an unnamed
                          button. A radiogroup names the official and exposes which star is chosen. */}
                      <div
                        className="flex items-center gap-1"
                        role="radiogroup"
                        aria-label={t('rateRefereeStars', { name: member.userName })}
                        data-testid={`referee-rating-${member.id}`}
                      >
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            role="radio"
                            aria-checked={(refereeRatings[member.id] ?? 0) === star}
                            aria-label={t('starsOutOfFive', { value: star })}
                            onClick={() => handleRatingChange(member.id, star)}
                            className="p-0.5 transition-colors focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
                            disabled={isSubmitting}
                          >
                            <Icon
                              name="Star"
                              size={20}
                              className={
                                (refereeRatings[member.id] ?? 0) >= star
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-gray-300'
                              }
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{t('Rating is optional. Click a star to rate, click again to clear.')}</p>
              </div>
            )}

            {/* Attachment */}
            <MatchAttachmentField
              existingUrl={match?.attachmentUrl ?? null}
              onChange={handleAttachmentChange}
              disabled={isSubmitting}
            />

            {/* Comments */}
            <div className="space-y-2">
              <label htmlFor="complete-match-comments" className="block text-sm font-medium text-foreground">
                {t('Comments (Optional)')}
              </label>
              <textarea
                id="complete-match-comments"
                name="comments"
                value={formData?.comments}
                onChange={handleInputChange}
                placeholder={t('Add any additional notes or observations about the match...')}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors resize-none"
                disabled={isSubmitting}
                data-testid="complete-match-comments"
              />
            </div>

            {submitStatus?.message && (
              <div className={`p-3 rounded-lg text-sm ${submitStatus?.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'}`} data-testid="complete-match-status">
                {submitStatus?.message}
              </div>
            )}
          </DialogBody>

          {/* Action Buttons */}
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="complete-match-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="success"
              loading={isSubmitting}
              disabled={isSubmitting}
              iconName="CheckCircle"
              data-testid="complete-match-submit-btn"
            >
              {isSubmitting ? t('Submitting...') : t('Submit as Complete')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteMatchModal;
